import { useState } from "react";

import { msg, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import DotsHorizontal from "@uui/DotsHorizontal";
import DownloadIcon from "@uui/Download01";
import EditIcon from "@uui/Edit02";
import FileXIcon from "@uui/FileX03";
import SendIcon from "@uui/Send01";

import { useMediaQuery } from "@mui/material";
import Divider from "@mui/material/Divider";

import HeaderButton from "@app/analytics/components/reports/HeaderButton";
import RemoveTopicReportConfirmationModal from "@app/analytics/components/reports/RemoveTopicReportConfirmationModal";
import RenameReportModal from "@app/analytics/components/reports/RenameReportModal";
import useUpdateReportName from "@app/analytics/hooks/meetings/useUpdateReportName";
import useRemoveTopicReport from "@app/analytics/hooks/topics/useRemoveTopicReport";
import theme from "@app/analytics/theme";
import useTopicReportStore from "@app/analytics/TopicReport/hooks/useTopicReportStore";
import type { Topic } from "@app/analytics/types";
import { toast } from "@app/common/hooks/useToasts";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Menu from "@rui/Menu";
import MenuItem from "@rui/MenuItem";
import { MobileOnlyColumn } from "@rui/Stack";

import DownloadLink from "./Download/DownloadLink";
import SendViaMenuDialog from "./SendVia/SendViaMenuDialog";

const ReportActionsMenu = ({
  topic,
  shared,
}: {
  topic: Topic;
  shared: boolean;
}) => {
  const { _ } = useLingui();
  const isDesktopView = useMediaQuery(theme.breakpoints.up("sm"));
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [sendViaOpen, setSendViaOpen] = useState(false);

  const setShowRemoveFeedbackModal = useTopicReportStore(
    (state) => state.setShowRemoveFeedbackModal
  );

  const { removeTopicReport, isRemoving } = useRemoveTopicReport();

  const closeRenameModal = () => setRenameModalOpen(false);

  const { updateTopicName, loadingTopic: isUpdatingName } = useUpdateReportName(
    topic,
    {
      onSettled: closeRenameModal,
    }
  );

  const {
    anchorElement,
    onClick,
    onClose: onMenuClose,
    open,
  } = useAnchorElement();

  const openModal = () => {
    onMenuClose();
    setConfirmationModalOpen(true);
  };

  const closeModal = () => setConfirmationModalOpen(false);

  const handleEditReportTitle = () => {
    onMenuClose();
    if (isDesktopView) {
      const element = document.getElementById("topic-report-title");
      element?.click();
    } else {
      setRenameModalOpen(true);
    }
  };

  const handleSendVia = () => {
    setSendViaOpen(true);
    onMenuClose();
  };

  const handleRemoveReport = () => {
    return removeTopicReport(
      {
        topicId: topic.id,
        ownerUserId: topic.ownerUserId,
      },
      {
        onSuccess: () => setShowRemoveFeedbackModal(true),
        onError: () => {
          closeModal();
          toast.error(
            "Something went wrong and your report could not be removed."
          );
        },
      }
    );
  };

  return (
    <>
      <HeaderButton Icon={<DotsHorizontal />} onClick={onClick} />
      <Menu anchorEl={anchorElement} open={open} onClose={onMenuClose}>
        {!shared && (
          <MobileOnlyColumn sx={{ display: "contents" }}>
            <MenuItem onClick={onMenuClose} icon={<DownloadIcon />}>
              <DownloadLink
                sx={{ padding: 0 }}
                topic={topic}
                onClose={onMenuClose}
                linkText={_(msg`Download`)}
              />
            </MenuItem>
            <MenuItem icon={<SendIcon />} onClick={handleSendVia}>
              <Trans>Send via...</Trans>
            </MenuItem>
            <Divider />
          </MobileOnlyColumn>
        )}
        {!shared && (
          <MenuItem icon={<EditIcon />} onClick={handleEditReportTitle}>
            <Trans>Rename Report</Trans>
          </MenuItem>
        )}
        {!shared && <Divider sx={{ display: { xxs: "none", sm: "block" } }} />}
        <MenuItem icon={<FileXIcon />} onClick={openModal} type="error">
          <Trans>Remove Report</Trans>
        </MenuItem>
      </Menu>
      <RemoveTopicReportConfirmationModal
        loading={isRemoving}
        open={confirmationModalOpen}
        onClose={closeModal}
        onConfirm={handleRemoveReport}
      />
      <RenameReportModal
        loading={isUpdatingName}
        open={renameModalOpen}
        reportTitle={topic.title}
        onConfirm={updateTopicName}
        onClose={closeRenameModal}
        type="topic"
      />
      <SendViaMenuDialog
        open={sendViaOpen}
        onClose={() => setSendViaOpen(false)}
      />
    </>
  );
};

export default ReportActionsMenu;
